import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
export interface VariableOption {
    name: string;
    label: string;
    section?: string;
}

interface MentionListProps {
    items: VariableOption[];
    command: (item: { id: string; label: string }) => void;
}

const MentionList = React.forwardRef<
    { onKeyDown: (event: KeyboardEvent) => boolean },
    MentionListProps
>(({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    React.useEffect(() => {
        setSelectedIndex(0);
    }, [items]);

    const selectItem = (index: number) => {
        const item = items[index];
        if (item) {
            command({ id: item.name, label: item.label });
        }
    };

    React.useImperativeHandle(ref, () => ({
        onKeyDown: (event: KeyboardEvent) => {
            if (event.key === 'ArrowUp') {
                setSelectedIndex((prev) =>
                    (prev + items.length - 1) % items.length
                );
                return true;
            }
            if (event.key === 'ArrowDown') {
                setSelectedIndex((prev) => (prev + 1) % items.length);
                return true;
            }
            if (event.key === 'Enter') {
                selectItem(selectedIndex);
                return true;
            }
            return false;
        },
    }));

    if (!items.length) {
        return (
            <div className="bg-popover border border-border rounded-md shadow-md p-2 text-sm text-muted-foreground">
                No variables found
            </div>
        );
    }

    return (
        <div className="bg-popover border border-border rounded-md shadow-md py-1 min-w-[200px] max-h-[300px] overflow-y-auto">
            {items.map((item, index) => (
                <button
                    key={item.name}
                    type="button"
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-2 ${
                        index === selectedIndex
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-muted'
                    }`}
                    onClick={() => selectItem(index)}
                >
                    <span>{item.label}</span>
                    <span className="text-xs text-muted-foreground">
                        @{item.name}
                    </span>
                </button>
            ))}
        </div>
    );
});

MentionList.displayName = 'MentionList';

export function createMentionSuggestion(
    variables: VariableOption[]
): Omit<SuggestionOptions, 'editor'> {
    return {
        items: ({ query }) => {
            const lower = query.toLowerCase();
            return variables.filter(
                (v) =>
                    v.label.toLowerCase().includes(lower) ||
                    v.name.toLowerCase().includes(lower)
            );
        },
        render: () => {
            let container: HTMLDivElement | null = null;
            let root: Root | null = null;
            let listRef: { onKeyDown: (event: KeyboardEvent) => boolean } | null = null;

            return {
                onStart: (props: SuggestionProps) => {
                    container = document.createElement('div');
                    container.style.position = 'absolute';
                    container.style.zIndex = '50';
                    document.body.appendChild(container);

                    const rect = props.clientRect?.();
                    if (rect) {
                        container.style.top = `${rect.bottom + window.scrollY}px`;
                        container.style.left = `${rect.left + window.scrollX}px`;
                    }

                    root = createRoot(container);
                    root.render(
                        <MentionList
                            ref={(r) => { listRef = r; }}
                            items={props.items as VariableOption[]}
                            command={props.command}
                        />
                    );
                },

                onUpdate: (props: SuggestionProps) => {
                    const rect = props.clientRect?.();
                    if (rect && container) {
                        container.style.top = `${rect.bottom + window.scrollY}px`;
                        container.style.left = `${rect.left + window.scrollX}px`;
                    }

                    root?.render(
                        <MentionList
                            ref={(r) => { listRef = r; }}
                            items={props.items as VariableOption[]}
                            command={props.command}
                        />
                    );
                },

                onKeyDown: (props: { event: KeyboardEvent }) => {
                    if (props.event.key === 'Escape') {
                        container?.remove();
                        return true;
                    }
                    return listRef?.onKeyDown(props.event) ?? false;
                },

                onExit: () => {
                    root?.unmount();
                    container?.remove();
                },
            };
        },
    };
}
